import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowRight, PiggyBank, Clock3, Gauge, MailCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { SectionHead, Reveal } from "@/components/Reveal";
import { Slider } from "@/components/ui/slider";
import { scrollToId } from "@/lib/scroll";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (n) => "$" + Math.round(n).toLocaleString("en-US");

export default function Calculator() {
  const [spend, setSpend] = useState(12000);
  const [workloads, setWorkloads] = useState(40);
  const [captureEmail, setCaptureEmail] = useState("");
  const [captureState, setCaptureState] = useState("idle");
  const [honeypot, setHoneypot] = useState("");

  const monthlySavings = spend * 0.3;
  const annualSavings = monthlySavings * 12;
  const hoursSaved = Math.round(workloads * 1.5);

  const sendEstimate = async () => {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(captureEmail.trim())) {
      toast.error("Enter a valid email address");
      return;
    }
    setCaptureState("sending");
    try {
      const { data } = await axios.post(`${API}/estimates`, {
        email: captureEmail.trim(),
        monthly_spend: spend,
        workloads,
        monthly_savings: monthlySavings,
        annual_savings: annualSavings,
        hours_saved: hoursSaved,
        website: honeypot,
      });
      setCaptureState("done");
      toast.success(
        data.emailed
          ? "Estimate sent — check your inbox."
          : "Estimate saved. Our team will share your full breakdown."
      );
    } catch {
      setCaptureState("idle");
      toast.error("Could not save your estimate. Please try again.");
    }
  };

  const getBreakdown = () => {
    window.dispatchEvent(
      new CustomEvent("nexorbit:prefill-message", {
        detail: `Savings estimate: monthly spend ${fmt(spend)}, ${workloads} workloads — est. savings ${fmt(monthlySavings)}/mo (${fmt(annualSavings)}/yr), ~${hoursSaved} ops hrs/mo reclaimed. I'd like the full breakdown.`,
      })
    );
    scrollToId("#contact");
  };

  return (
    <section id="calculator" data-testid="calculator-section" className="py-24 sm:py-32 bg-nexlight">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <SectionHead
          chapter="05"
          overline="Savings Estimator"
          title="What Could You Save on Cloud?"
          sub="Most teams over-provision. Slide to your current footprint and see what disciplined optimization typically unlocks."
          testId="calculator-heading"
        />

        <Reveal delay={0.1} className="mt-16">
          <div className="relative overflow-hidden rounded-3xl bg-nexblue p-8 sm:p-12 lg:p-14">
            <div className="orbit-ring w-[480px] h-[480px] -right-40 -top-40" style={{ borderColor: "rgba(0,168,232,0.15)" }} aria-hidden="true" />
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(255,149,0,0.08),transparent_55%)]" aria-hidden="true" />

            <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
              <div>
                <div className="mb-12">
                  <div className="flex items-baseline justify-between mb-4">
                    <label htmlFor="spend-slider" className="text-sm font-semibold uppercase tracking-[0.2em] text-white/60">
                      Monthly Cloud Spend
                    </label>
                    <span className="font-display text-2xl font-bold text-white" data-testid="spend-value">{fmt(spend)}</span>
                  </div>
                  <Slider
                    id="spend-slider"
                    data-testid="spend-slider"
                    min={1000}
                    max={100000}
                    step={500}
                    value={[spend]}
                    onValueChange={([v]) => setSpend(v)}
                    className="[&>span:first-child]:h-2 [&>span:first-child]:bg-white/15 [&>span:first-child>span]:bg-nexcyan [&>span:last-child]:h-5 [&>span:last-child]:w-5 [&>span:last-child]:border-2 [&>span:last-child]:border-nexcyan [&>span:last-child]:bg-white"
                  />
                  <div className="mt-2 flex justify-between text-xs text-white/40">
                    <span>$1,000</span><span>$100,000</span>
                  </div>
                </div>

                <div>
                  <div className="flex items-baseline justify-between mb-4">
                    <label htmlFor="workloads-slider" className="text-sm font-semibold uppercase tracking-[0.2em] text-white/60">
                      Cloud Workloads
                    </label>
                    <span className="font-display text-2xl font-bold text-white" data-testid="workloads-value">{workloads}</span>
                  </div>
                  <Slider
                    id="workloads-slider"
                    data-testid="workloads-slider"
                    min={5}
                    max={500}
                    step={5}
                    value={[workloads]}
                    onValueChange={([v]) => setWorkloads(v)}
                    className="[&>span:first-child]:h-2 [&>span:first-child]:bg-white/15 [&>span:first-child>span]:bg-nexorange [&>span:last-child]:h-5 [&>span:last-child]:w-5 [&>span:last-child]:border-2 [&>span:last-child]:border-nexorange [&>span:last-child]:bg-white"
                  />
                  <div className="mt-2 flex justify-between text-xs text-white/40">
                    <span>5</span><span>500</span>
                  </div>
                </div>

                <button
                  onClick={() => scrollToId("#contact")}
                  data-testid="calculator-cta-btn"
                  className="group mt-12 inline-flex items-center gap-2 rounded-full bg-nexorange px-7 py-3.5 text-sm font-bold text-nexblue transition-transform duration-300 hover:scale-[1.05] hover:shadow-[0_12px_32px_rgba(255,149,0,0.4)]"
                >
                  Get Your Free Assessment
                  <ArrowRight size={17} className="transition-transform duration-300 group-hover:translate-x-1" />
                </button>
              </div>

              <div className="flex flex-col justify-center gap-5">
                {[
                  { icon: PiggyBank, label: "Estimated Annual Savings", value: fmt(annualSavings), testId: "annual-savings-value", accent: "text-nexorange" },
                  { icon: Gauge, label: "Monthly Cost Reduction", value: fmt(monthlySavings) + " /mo", testId: "monthly-savings-value", accent: "text-nexcyan" },
                  { icon: Clock3, label: "Ops Hours Reclaimed", value: hoursSaved + " hrs /mo", testId: "hours-saved-value", accent: "text-nexcyan" },
                ].map(({ icon: Icon, label, value, testId, accent }) => (
                  <motion.div
                    key={label}
                    layout
                    className="flex items-center gap-5 rounded-2xl border border-white/10 bg-white/[0.05] px-7 py-6 backdrop-blur-sm"
                    data-testid={testId + "-card"}
                  >
                    <span className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-white/10 ${accent}`}>
                      <Icon size={22} />
                    </span>
                    <span>
                      <span className="block text-xs font-semibold uppercase tracking-[0.2em] text-white/50">{label}</span>
                      <motion.span
                        key={value}
                        initial={{ opacity: 0.4, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.35 }}
                        className="block font-display text-2xl sm:text-3xl font-bold text-white tabular-nums"
                        data-testid={testId}
                      >
                        {value}
                      </motion.span>
                    </span>
                  </motion.div>
                ))}
                <p className="text-xs leading-relaxed text-white/40">
                  Based on the ~30% average cost savings our optimization engagements typically uncover. Your free assessment produces exact figures.
                </p>

                <div className="rounded-2xl border border-white/10 bg-white/[0.05] p-5" data-testid="calculator-capture">
                  <input
                    type="text"
                    name="website"
                    value={honeypot}
                    onChange={(e) => setHoneypot(e.target.value)}
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    className="absolute -left-[9999px] h-0 w-0 opacity-0"
                  />
                  <p className="text-sm font-semibold text-white">Want a copy of this estimate?</p>
                  {captureState === "done" ? (
                    <div className="mt-3">
                      <p className="flex items-center gap-2 text-sm text-nexcyan" data-testid="calculator-capture-success">
                        <MailCheck size={16} /> Saved for {captureEmail}
                      </p>
                      <button
                        onClick={getBreakdown}
                        data-testid="calculator-breakdown-btn"
                        className="group mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-nexorange transition-colors duration-300 hover:text-white"
                      >
                        Get your full breakdown
                        <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
                      </button>
                    </div>
                  ) : (
                    <div className="mt-3 flex flex-col sm:flex-row gap-3">
                      <input
                        type="email"
                        value={captureEmail}
                        onChange={(e) => setCaptureEmail(e.target.value)}
                        placeholder="you@company.com"
                        data-testid="calculator-email-input"
                        className="h-11 flex-1 rounded-full border border-white/15 bg-white/10 px-5 text-sm text-white placeholder:text-white/40 outline-none transition-colors duration-300 focus:border-nexcyan"
                      />
                      <button
                        onClick={sendEstimate}
                        disabled={captureState === "sending"}
                        data-testid="calculator-email-btn"
                        className="inline-flex items-center justify-center gap-2 rounded-full bg-nexcyan px-6 py-2.5 text-sm font-bold text-nexblue transition-transform duration-300 hover:scale-[1.04] disabled:opacity-60"
                      >
                        {captureState === "sending" ? <Loader2 size={15} className="animate-spin" /> : <MailCheck size={15} />}
                        {captureState === "sending" ? "Saving..." : "Email Me This"}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
